/**
 * Grabador de backup de la llamada en el navegador.
 *
 * Mezcla el micrófono del agente (track local) y la voz del cliente (track
 * remoto de LiveKit) con Web Audio en un único stream, y lo graba con
 * MediaRecorder usando timeslice. Cada chunk se persiste en IndexedDB apenas
 * llega, de modo que un crash a mitad de llamada no pierde lo ya grabado.
 *
 * Uso (una instancia por llamada):
 *   const rec = new CallRecorder();
 *   rec.setRemoteTrack(remoteMediaStreamTrack);      // en cualquier momento
 *   await rec.start({ localTrack, meta: { call_uuid, company_id, recorded_at } });
 *   await rec.stop();                                 // al colgar
 */

import { createRecording, appendChunk, markComplete, selfTest } from './db';

const PREFERRED_MIME = 'audio/webm;codecs=opus';
const FALLBACK_MIME = 'audio/webm';
const TIMESLICE_MS = 5000; // un chunk cada 5s => menor pérdida ante crash (costo despreciable)

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return null;
  }
  if (MediaRecorder.isTypeSupported(PREFERRED_MIME)) {
    return PREFERRED_MIME;
  }
  if (MediaRecorder.isTypeSupported(FALLBACK_MIME)) {
    return FALLBACK_MIME;
  }
  return null;
}

export class CallRecorder {
  constructor() {
    this.audioContext = null;
    this.destination = null;
    this.sources = [];
    this.recorder = null;
    this.pendingRemoteTracks = [];
    this.pendingWrites = [];
    this.recordingId = null;
    this.seq = 0;
    this.started = false;
    this.stopped = false;
    this.mimeType = pickMimeType();
  }

  isSupported() {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    return !!this.mimeType && !!AudioCtx && typeof indexedDB !== 'undefined';
  }

  /**
   * Verifica ANTES de colocar la llamada que este navegador puede grabar el
   * backup: soporte de APIs, pipeline Web Audio + MediaRecorder e IndexedDB
   * escribible. Devuelve true/false (nunca lanza). Si devuelve false, la
   * llamada NO debe iniciarse.
   */
  async preflight() {
    if (!this.isSupported()) {
      console.error('CallRecorder.preflight: el navegador no soporta la grabación de backup (MediaRecorder/AudioContext/IndexedDB)');
      return false;
    }

    // 1) Pipeline Web Audio + MediaRecorder.
    let ctx = null;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      ctx = new AudioCtx();
      if (ctx.state === 'suspended' && ctx.resume) {
        await ctx.resume();
      }
      const dest = ctx.createMediaStreamDestination();
      const testRecorder = new MediaRecorder(dest.stream, { mimeType: this.mimeType });
      testRecorder.start();
      testRecorder.stop();
    } catch (e) {
      console.error('CallRecorder.preflight: no se pudo inicializar el pipeline de grabación', e);
      return false;
    } finally {
      if (ctx) {
        try { await ctx.close(); } catch (e) { /* ignore */ }
      }
    }

    // 2) IndexedDB escribible (incógnito/cuota/bloqueo).
    try {
      await selfTest();
    } catch (e) {
      console.error('CallRecorder.preflight: IndexedDB no está disponible para el backup', e);
      return false;
    }

    return true;
  }

  _addTrack(track) {
    if (!track || !this.audioContext || !this.destination) {
      return;
    }
    try {
      const stream = new MediaStream([track]);
      const source = this.audioContext.createMediaStreamSource(stream);
      source.connect(this.destination);
      this.sources.push(source);
    } catch (e) {
      console.warn('CallRecorder: no se pudo agregar un track al mix', e);
    }
  }

  /** Registra el track remoto (cliente). Puede llamarse antes o después de start(). */
  setRemoteTrack(track) {
    if (!track) {
      return;
    }
    if (this.started) {
      this._addTrack(track);
    } else {
      this.pendingRemoteTracks.push(track);
    }
  }

  async start({ localTrack, meta }) {
    if (this.started || this.stopped) {
      return;
    }
    if (!this.isSupported()) {
      console.warn('CallRecorder: MediaRecorder/AudioContext/IndexedDB no soportados; no se graba backup');
      return;
    }
    if (!meta || !meta.call_uuid) {
      console.warn('CallRecorder: falta call_uuid; no se graba backup');
      return;
    }

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    this.audioContext = new AudioCtx();
    // El contexto puede quedar 'suspended' hasta un gesto del usuario; la
    // llamada arranca tras un click del agente, así que se puede reanudar.
    if (this.audioContext.state === 'suspended' && this.audioContext.resume) {
      try { await this.audioContext.resume(); } catch (e) { /* ignore */ }
    }
    this.destination = this.audioContext.createMediaStreamDestination();

    this._addTrack(localTrack);
    this.pendingRemoteTracks.forEach(t => this._addTrack(t));
    this.pendingRemoteTracks = [];

    this.recordingId = meta.call_uuid;
    const extension = (this.mimeType && this.mimeType.indexOf('webm') !== -1) ? 'webm' : 'bin';

    await createRecording({
      id: this.recordingId,
      call_uuid: meta.call_uuid,
      company_id: meta.company_id || null,
      recorded_at: meta.recorded_at,
      direction: meta.direction || 'outbound',
      mimeType: this.mimeType,
      extension,
    });

    this.recorder = new MediaRecorder(this.destination.stream, { mimeType: this.mimeType });
    this.recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        const seq = this.seq;
        this.seq += 1;
        const write = appendChunk(this.recordingId, seq, event.data)
          .catch(e => console.error('CallRecorder: error guardando chunk', e));
        this.pendingWrites.push(write);
      }
    };
    this.recorder.start(TIMESLICE_MS);
    this.started = true;
  }

  async stop() {
    if (this.stopped) {
      return;
    }
    this.stopped = true;

    try {
      if (this.recorder && this.recorder.state !== 'inactive') {
        await new Promise((resolve) => {
          this.recorder.onstop = resolve;
          this.recorder.stop(); // emite el último ondataavailable y luego onstop
        });
      }
    } catch (e) {
      console.warn('CallRecorder: error al detener el recorder', e);
    }

    // Esperar a que todos los chunks pendientes terminen de escribirse.
    try {
      await Promise.all(this.pendingWrites);
    } catch (e) { /* ya logueado en cada write */ }

    if (this.recordingId && this.started) {
      try {
        await markComplete(this.recordingId);
      } catch (e) {
        console.warn('CallRecorder: no se pudo marcar como completa', e);
      }
    }

    // Limpieza de Web Audio.
    this.sources.forEach((s) => { try { s.disconnect(); } catch (e) { /* ignore */ } });
    this.sources = [];
    if (this.audioContext) {
      try { await this.audioContext.close(); } catch (e) { /* ignore */ }
      this.audioContext = null;
    }
  }
}



// WEBPACK FOOTER //
// ./src/services/callRecording/recorder.js